/*
 * Tenant branding to CSS variables. Tailwind v4 compiles `bg-brand-600` to
 * `background-color: var(--color-brand-600)`, so overriding the `--color-brand-*` tokens on
 * `<html>` re-colors every utility at runtime without rebuilding the stylesheet.
 */

export const BRAND_SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type BrandShade = (typeof BRAND_SHADES)[number];

/** Lightness ladder (percent) for the shades; `600` is the primary action color. */
const LIGHTNESS: Record<BrandShade, number> = {
  50: 97,
  100: 93,
  200: 86,
  300: 76,
  400: 63,
  500: 52,
  600: 44,
  700: 37,
  800: 30,
  900: 24,
  950: 15,
};

export const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hexToHsl(hex: string): Hsl | null {
  if (!HEX_COLOR.test(hex)) return null;
  let raw = hex.slice(1);
  if (raw.length === 3) raw = raw.replace(/./g, (c) => c + c);
  const r = Number.parseInt(raw.slice(0, 2), 16) / 255;
  const g = Number.parseInt(raw.slice(2, 4), 16) / 255;
  const b = Number.parseInt(raw.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l: l * 100 };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
}

const hsl = ({ h, s, l }: Hsl): string => `hsl(${h} ${s.toFixed(1)}% ${l.toFixed(1)}%)`;

/**
 * A full shade palette from one primary color: hue and saturation come from the input, the
 * lightness follows a fixed ladder so contrast between text and surfaces stays predictable
 * (light shades are slightly desaturated so tinted backgrounds do not glow).
 */
export function paletteFromHex(hex: string): Record<BrandShade, string> | null {
  const base = hexToHsl(hex);
  if (!base) return null;
  const out = {} as Record<BrandShade, string>;
  for (const shade of BRAND_SHADES) {
    const l = LIGHTNESS[shade];
    const s = shade <= 200 ? Math.min(base.s, 70) : base.s;
    out[shade] = hsl({ h: base.h, s, l });
  }
  return out;
}

/** Writes (or clears, with `null`) the brand tokens on the given root element. */
export function applyBrandColor(root: HTMLElement, primaryColor: string | null | undefined): void {
  const palette = primaryColor ? paletteFromHex(primaryColor) : null;
  for (const shade of BRAND_SHADES) {
    const name = `--color-brand-${shade}`;
    if (palette) root.style.setProperty(name, palette[shade]);
    else root.style.removeProperty(name);
  }
}
